import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CompanyShell } from "@/components/CompanyShell";
import { ScoreRing } from "@/components/ScoreRing";
import { StatusBadge } from "@/components/StatusBadge";
import { UploadCloud, Search, Users, ArrowRight, Sparkles, Briefcase } from "lucide-react";

export default function CompanyDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, positions: 0, candidates: [] });
  const [jobsCount, setJobsCount] = useState(0);

  useEffect(() => {
    api.get("/company/database").then((r) => setData(r.data)).catch(() => {});
    api.get("/company/jobs").then((r) => setJobsCount((r.data.jobs || []).length)).catch(() => {});
  }, []);

  return (
    <CompanyShell>
      <p className="text-slate-500">Dobrý deň,</p>
      <h1 className="font-head text-3xl font-bold tracking-tight">{user?.name} 👋</h1>
      <p className="mt-2 text-slate-600">Čo chcete urobiť?</p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard onClick={() => nav("/company/upload")} testId="action-upload" icon={UploadCloud}
          title="Nahrať CV kandidáta" sub="Budujte svoju databázu talentov" color="bg-blue-700" />
        <ActionCard onClick={() => nav("/company/find")} testId="action-find" icon={Search}
          title="Nájsť vhodného kandidáta" sub="Podľa konkrétnej pracovnej pozície" color="bg-emerald-600" />
        <ActionCard onClick={() => nav("/company/database")} testId="action-database" icon={Users}
          title="Databáza kandidátov" sub="Prehľad všetkých profilov" color="bg-slate-800" />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <Stat label="Kandidáti v databáze" value={data.count} />
        <button data-testid="stat-my-jobs" onClick={() => nav("/company/jobs")}
          className="text-left rounded-3xl bg-white border border-slate-200 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-[0.98]">
          <div className="flex items-center justify-between">
            <div className="font-head text-4xl font-bold text-blue-700">{jobsCount}</div>
            <div className="h-10 w-10 rounded-2xl bg-blue-50 flex items-center justify-center"><Briefcase className="text-blue-700" size={20} /></div>
          </div>
          <div className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1 font-medium">Moje pracovné ponuky <ArrowRight size={14} /></div>
        </button>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-head text-xl font-semibold">Posledné profily</h2>
        <button onClick={() => nav("/company/database")} className="text-sm font-semibold text-blue-700 inline-flex items-center gap-1">Všetky <ArrowRight size={15} /></button>
      </div>

      {data.candidates.length === 0 ? (
        <div className="mt-4 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50"><Sparkles className="text-blue-700" /></div>
          <p className="mt-4 font-semibold text-slate-800">Zatiaľ žiadne profily</p>
          <p className="text-sm text-slate-500 mt-1">Nahrajte prvé CV a AI z neho spraví profil.</p>
          <button data-testid="empty-upload-btn" onClick={() => nav("/company/upload")}
            className="mt-5 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 font-semibold transition-colors active:scale-95">
            <UploadCloud size={17} /> Nahrať CV
          </button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.candidates.slice(0, 6).map((c) => (
            <CandidateRow key={c.id} c={c} onClick={() => nav(`/company/candidate/${c.id}`)} />
          ))}
        </div>
      )}
    </CompanyShell>
  );
}

function ActionCard({ onClick, testId, icon: Icon, title, sub, color }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="text-left rounded-3xl bg-white border border-slate-200 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-[0.98]">
      <div className={`h-12 w-12 rounded-2xl ${color} flex items-center justify-center`}><Icon className="text-white" size={22} /></div>
      <h3 className="mt-4 font-head font-semibold text-lg">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{sub}</p>
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
      <div className="font-head text-4xl font-bold text-blue-700">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export function CandidateRow({ c, onClick }) {
  return (
    <button data-testid={`candidate-row-${c.id}`} onClick={onClick}
      className={`w-full flex items-center gap-4 rounded-2xl bg-white border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all text-left ${c.archived ? "opacity-70" : ""}`}>
      {c.photo_url ? (
        <img src={c.photo_url} alt={c.name} className="h-12 w-12 rounded-full object-cover" />
      ) : (
        <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-head font-bold">{(c.name || "?").trim().charAt(0).toUpperCase()}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-head font-semibold text-slate-900 truncate">{c.name}</div>
        <div className="text-sm text-slate-500 truncate">{c.title}</div>
        <div className="mt-1.5"><StatusBadge profile={c} /></div>
      </div>
      <ScoreRing value={c.career_score || 0} size={52} stroke={5} showLabel={false} />
    </button>
  );
}
